import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState, useCallback } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { Cities } from '@/types/Cities';
import { GoogleMap, Marker, Autocomplete, Circle } from "@react-google-maps/api";
import { toast } from 'sonner';
import api from '@/lib/axiosInstance';
import { Checkbox } from '@/components/ui/checkbox';
import Helper from '@/lib/helper';
import LabelWithAsterisk from '@/components/LabelWithAsterisk';
import { useGoogleMaps } from '@/components/GoogleMapsProvider';
import type { States } from '@/types/States';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MapIcon } from 'lucide-react';
import type { Districts } from '@/types/Districts';
import type { Subdistricts } from '@/types/Subdistricts';
import type { StaticListings } from '@/types/StaticListings';
import Details from '../view/details';
import type { PropertyListings } from '@/types/PropertyListings';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Info } from "lucide-react"
import { Switch } from '@/components/ui/switch';
import type { LandTitleTypes } from '@/types/LandTitleTypes';

interface _LocationMappingDetails {
    states: States[];
    manualAddress: boolean;
    setManualAddress: React.Dispatch<React.SetStateAction<boolean>>;
    rawAddress: string;
    setRawAddress: React.Dispatch<React.SetStateAction<string>>;
    selectedState: number | null;
    setSelectedState: React.Dispatch<React.SetStateAction<number | null>>;
    selectedCity: number | null;
    setSelectedCity: React.Dispatch<React.SetStateAction<number | null>>;
    selectedTownship: string;
    setSelectedTownship: React.Dispatch<React.SetStateAction<string>>;
    selectedStreet: string;
    setSelectedStreet: React.Dispatch<React.SetStateAction<string>>;
    selectedPostalCode: string;
    setSelectedPostalCode: React.Dispatch<React.SetStateAction<string>>;
    selectedUnitNumber: string;
    setSelectedUnitNumber: React.Dispatch<React.SetStateAction<string>>;
    noTownship: boolean;
    setNoTownship: React.Dispatch<React.SetStateAction<boolean>>; 
    coord: {
        lat: number | null;
        lng: number | null
    };
    setCoord: React.Dispatch<React.SetStateAction<{
        lat: number | null;
        lng: number | null
    }>>;
    selectedDistrict: string;
    setSelectedDistrict: React.Dispatch<React.SetStateAction<string>>;
    selectedSubdistrict: string;
    setSelectedSubdistrict: React.Dispatch<React.SetStateAction<string>>;
    selectedTitleTypeCode: string;
    setSelectedTitleTypeCode: React.Dispatch<React.SetStateAction<string>>;
    jupemNo: string;
    setJupemNo: React.Dispatch<React.SetStateAction<string>>;
    sizeOfLand: string;
    setSizeOfLand: React.Dispatch<React.SetStateAction<string>>;
    lotNo: string;
    setLotNo: React.Dispatch<React.SetStateAction<string>>;
    ownershipNumber: string;
    setOwnershipNumber: React.Dispatch<React.SetStateAction<string>>;
    districts: Districts[];
    subdistricts: Subdistricts[];
    landTitleTypes: LandTitleTypes[];
    setReferenceListing: React.Dispatch<React.SetStateAction<PropertyListings | null>>;
    markAsDuplicate?: boolean;
    setMarkAsDuplicate?: React.Dispatch<React.SetStateAction<boolean>>;
    edit?: boolean;
    listing?: PropertyListings | null;
    proof?: File | null;
    setProof?: React.Dispatch<React.SetStateAction<File | null>>;
    excludeMapPin: boolean;
    setExcludeMapPin: React.Dispatch<React.SetStateAction<boolean>>;
    selectedPropertyType: number | null;
    selectedPropertySubType: number | null;
    radius: number | null;
    setRadius: React.Dispatch<React.SetStateAction<number | null>>;
    electricityBill: File | string | null;
    setElectricityBill: React.Dispatch<React.SetStateAction<File | string | null>>;
    waterBill: File | string | null;
    setWaterBill: React.Dispatch<React.SetStateAction<File | string | null>>;
    useTrueLocation: boolean;
    setUseTrueLocation: React.Dispatch<React.SetStateAction<boolean>>;
    selectedListingType: number | null;
}

export interface LocationMappingDetailsRef {
    callback: () => Promise<boolean>;
}

const LocationMappingDetails = forwardRef<LocationMappingDetailsRef, _LocationMappingDetails>(({
    states,
    manualAddress,
    setManualAddress,
    rawAddress,
    setRawAddress,
    selectedState,
    setSelectedState,
    selectedCity,
    setSelectedCity,
    selectedTownship,
    setSelectedTownship,
    selectedStreet,
    setSelectedStreet,
    selectedPostalCode,
    setSelectedPostalCode,
    selectedUnitNumber,
    setSelectedUnitNumber,
    noTownship,
    setNoTownship,
    coord,
    setCoord,
    selectedDistrict,
    setSelectedDistrict,
    selectedSubdistrict,
    setSelectedSubdistrict,
    selectedTitleTypeCode,
    setSelectedTitleTypeCode,
    jupemNo,
    setJupemNo,
    sizeOfLand,
    setSizeOfLand,
    lotNo,
    setLotNo,
    ownershipNumber,
    setOwnershipNumber,
    districts,
    subdistricts,
    landTitleTypes,
    setReferenceListing,
    markAsDuplicate,
    setMarkAsDuplicate,
    proof,
    setProof,
    listing,
    edit,
    excludeMapPin,
    setExcludeMapPin,
    selectedPropertyType,
    selectedPropertySubType,
    radius,
    setRadius,
    electricityBill,
    setElectricityBill,
    waterBill,
    setWaterBill,
    useTrueLocation,
    setUseTrueLocation,
    selectedListingType
}, ref) => {
    const { isLoaded } = useGoogleMaps();
    const [cities, setCities] = useState<Cities[] | null>(null);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [pendingCityName, setPendingCityName] = useState<string | null>(null);
    const geocoderRef = useRef<google.maps.Geocoder | null>(null);
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [listingDetails, setListingDetails] = useState<any>(null);
    const [loadingDetails, setLoadingDetails] = useState<boolean>(false);
    const [mapType, setMapType] = useState<'roadmap' | 'satellite'>('satellite');
    const [marking, setMarking] = useState<boolean>(false);
    const geocodeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isInitialMount = useRef(true);
    const hasInitialCoords = useRef(coord.lat !== null && coord.lng !== null);
    const isProgrammaticUpdate = useRef(false);
    const manualAddressRef = useRef(manualAddress);
    const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const mapRef = useRef<google.maps.Map | null>(null);
    const [isDraggingRadius, setIsDraggingRadius] = useState<boolean>(false);
    const circleRef = useRef<google.maps.Circle | null>(null);
    
    // const shouldHideLandDetails = selectedPropertyType === 5 && selectedPropertySubType === 20;
    // const shouldHideLandDetails = selectedListingType == 1 || selectedListingType == 2;
    const shouldHideLandDetails = false;

    if (!isLoaded) return <p>Loading map…</p>;

    const getCities = async () => {
        try {
            const response = await api.get(`/listings/states/${selectedState}/cities`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            setCities(response.data.cities);
        } catch (err: any) {
            const error = err.response ? err.response.data.error : err.message;
            toast.error(error);
        }
    }

    const reverseGeocode = async (lat: number, lng: number) => {
        if (!geocoderRef.current) {
            geocoderRef.current = new google.maps.Geocoder();
        }

        try {
            isProgrammaticUpdate.current = true;
            
            const result = await geocoderRef.current.geocode({
                location: { lat, lng }
            });

            if (result.results && result.results.length > 0) {
                const validResults = result.results.filter(r => 
                    !r.types.includes('plus_code') && r.formatted_address
                );
                
                const place = validResults.length > 0 ? validResults[0] : result.results[0];
                setRawAddress(place.formatted_address ?? '');
                
                place.address_components?.forEach((component) => {
                    const types = component.types;
                    
                    if (types.includes('street_number')) {
                        setSelectedUnitNumber(component.long_name);
                    }

                    if (types.includes('route')) {
                        setSelectedStreet(component.long_name);
                    }

                    if (types.includes('locality')) {
                        setPendingCityName(component.long_name);
                    }

                    if (types.includes('sublocality') || types.includes('sublocality_level_1')) {
                        setSelectedTownship(component.long_name);
                    }
                    
                    if (types.includes('administrative_area_level_2')) {
                        setSelectedDistrict(component.long_name);
                    }
                    
                    if (types.includes('administrative_area_level_3')) {
                        setSelectedSubdistrict(component.long_name);
                    }

                    if (types.includes('administrative_area_level_1')) {
                        const componentName = component.long_name.toLowerCase();
                        const state = states.find((x) =>
                            componentName.includes(x.name.toLowerCase())
                        );
                        if (state) {
                            setSelectedState(state.id);
                        }
                    }

                    if (types.includes('postal_code')) {
                        setSelectedPostalCode(component.long_name);
                    }
                });
            }
            
            setTimeout(() => {
                isProgrammaticUpdate.current = false;
            }, 100);
        } catch (error) {
            console.error('Reverse geocoding error:', error);
            isProgrammaticUpdate.current = false;
        }
    };

    const geocodeFromAddress = useCallback(async () => {
        if (!selectedState || !selectedCity || !selectedPostalCode) {
            return;
        }

        if (!geocoderRef.current) {
            geocoderRef.current = new google.maps.Geocoder();
        }

        try {
            const state = states.find(s => s.id === selectedState);
            const city = cities?.find(c => c.id === selectedCity);
            
            if (!state || !city) return;

            const addressParts = [
                selectedUnitNumber,
                selectedStreet,
                selectedTownship,
                city.name,
                selectedPostalCode,
                state.name,
                'Malaysia'
            ].filter(Boolean);

            const addressString = addressParts.join(', ');

            const result = await geocoderRef.current.geocode({
                address: addressString,
                componentRestrictions: { country: 'MY' }
            });

            if (result.results && result.results.length > 0) {
                const place = result.results[0];
                
                if (place.geometry?.location) {
                    const newLat = place.geometry.location.lat();
                    const newLng = place.geometry.location.lng();
                    
                    setCoord({
                        lat: newLat,
                        lng: newLng
                    });
                    removeError('coordinates');
                    
                    if (mapRef.current) {
                        mapRef.current.panTo({ lat: newLat, lng: newLng });
                        mapRef.current.setZoom(17);
                    }
                }
            }
        } catch (error) {
            console.error('Geocoding from address error:', error);
        }
    }, [selectedState, selectedCity, selectedPostalCode, selectedTownship, selectedStreet, selectedUnitNumber, states, cities]);

    
    const handleAddressSearch = async (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        if (!rawAddress.trim()) {
            toast.error('Please enter an address to search');
            return;
        }
        
        if (!geocoderRef.current) {
            geocoderRef.current = new google.maps.Geocoder();
        }
        
        try {
            isProgrammaticUpdate.current = true;
            
            const result = await geocoderRef.current.geocode({
                address: rawAddress,
                componentRestrictions: { country: 'MY' }
            });

            if (result.results && result.results.length > 0) {
                const place = result.results[0];
                setRawAddress(place.formatted_address ?? '');
                removeError('rawAddress');
                
                place.address_components?.forEach((component) => {
                    const types = component.types;
                    
                    if (types.includes('street_number')) {
                        setSelectedUnitNumber(component.long_name);
                    }

                    if (types.includes('route')) {
                        setSelectedStreet(component.long_name);
                    }

                    if (types.includes('locality')) {
                        setPendingCityName(component.long_name);
                    }

                    if (types.includes('sublocality') || types.includes('sublocality_level_1')) {
                        setSelectedTownship(component.long_name);
                    }
                    
                    if (types.includes('administrative_area_level_2')) {
                        setSelectedDistrict(component.long_name);
                    }
                    
                    if (types.includes('administrative_area_level_3')) {
                        setSelectedSubdistrict(component.long_name);
                    }

                    if (types.includes('administrative_area_level_1')) {
                        const componentName = component.long_name.toLowerCase();
                        const state = states.find((x) =>
                            componentName.includes(x.name.toLowerCase())
                        );
                        if (state) {
                            setSelectedState(state.id);
                        }
                    }

                    if (types.includes('postal_code')) {
                        setSelectedPostalCode(component.long_name);
                    }
                });
                
                if (place.geometry?.location) {
                    const newLat = place.geometry.location.lat();
                    const newLng = place.geometry.location.lng();
                    setCoord({
                        lat: newLat,
                        lng: newLng
                    });
                    removeError('coordinates');
                    
                    if (mapRef.current) {
                        mapRef.current.panTo({ lat: newLat, lng: newLng });
                        mapRef.current.setZoom(17);
                    }
                }
            } else {
                toast.error('No locations found for this address');
            }
            
            setTimeout(() => {
                isProgrammaticUpdate.current = false;
            }, 100);
        } catch (error) {
            toast.error('Failed to search address. Please try again.');
            isProgrammaticUpdate.current = false;
        }
    };

    const callback = async () => {
        try {
            var valErrors = [];

            if(!rawAddress) {
                valErrors.push('rawAddress');
            }

            if(!selectedState) {
                valErrors.push('state');
            }

            if(!selectedCity) {
                valErrors.push('city');
            }

            if(!selectedPostalCode) {
                valErrors.push('postcode');
            }

            if((!coord.lat || !coord.lng)) {
                valErrors.push('coordinates');
            }

            if(!useTrueLocation && (!radius || radius <= 0)) {
                valErrors.push('radius');
            }

            if(shouldHideLandDetails) {
                if(!selectedStreet) {
                    valErrors.push('street');
                }
                
                if(!selectedUnitNumber) {
                    valErrors.push('unitNumber');
                }
                
                if(!noTownship && !selectedTownship) {
                    valErrors.push('township');
                }
               
                if(!electricityBill && !listing?.electricityBill) {
                    valErrors.push('electricityBill');
                }
                if(!waterBill && !listing?.waterBill) {
                    valErrors.push('waterBill');
                }
            } else if(!markAsDuplicate) {
                if(!lotNo) {
                    valErrors.push('lotNo');
                }

                if(!selectedDistrict) {
                    valErrors.push('district');
                }

                if(!selectedSubdistrict) {
                    valErrors.push('subdistrict');
                }

                if(!selectedTitleTypeCode) {
                    valErrors.push('landTitleType');
                }

                if(!ownershipNumber) {
                    valErrors.push('ownershipNumber');
                }

                if(!sizeOfLand) {
                    valErrors.push('sizeOfLand');
                }

                if(!proof && !listing) {
                    valErrors.push('proof');
                }
            }
            setValidationErrors(valErrors);

            if(valErrors.length > 0) {
                throw new Error('Please complete all required fields.');
            }
            return true;
        } catch (err: any) {
            const error = err.response ? err.response.data.error : err.message;
            toast.error(error);
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
            return false;
        }
    }

    const toggleManualAddress = () => {
        setManualAddress(prev => !prev);
    };

    const toggleMapType = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
    
        const newType: google.maps.MapTypeId =
            mapType === google.maps.MapTypeId.ROADMAP
                ? google.maps.MapTypeId.SATELLITE
                : google.maps.MapTypeId.ROADMAP;
    
        setMapType(newType);
    
        if (mapRef.current) {
            mapRef.current.setMapTypeId(newType);
        }
    };

    const removeError = (field: string) => {
        setValidationErrors(prevErrors => prevErrors.filter(x => x !== field));
    };

    const markingAsDuplicate = () => {
        if(!listingDetails) {
            toast.error('Empty listing. Something went wrong. Please try again.');
        }
        setMarking(true);
        setReferenceListing(listingDetails);
        setMarkAsDuplicate?.(true);
        setTimeout(() => {
            setMarking(false);
            setDialogOpen(false);
        }, 1000);
    }

    const handleFileChange = (type: 'electricity' | 'water', file: File | null) => {
        if (!file) return;

        const isImage = file.type.startsWith('image/');
        const isPdf = file.type === 'application/pdf';

        if (!isImage && !isPdf) {
            toast.error('Only images and PDF files are allowed');
            return;
        }

        if (type === 'electricity') {
            setElectricityBill(file);
            removeError('electricityBill');
        } else {
            setWaterBill(file);
            removeError('waterBill');
        }
    };

    useImperativeHandle(ref, () => ({
        callback
    }));

    useEffect(() => {
        if(selectedState) {
            getCities();
        }
    }, [selectedState]);

    useEffect(() => {
        if (pendingCityName && cities?.length) {
            const city = cities.find((x) => x.name === pendingCityName);
            if (city) {
                setSelectedCity(city.id);
            }
            setPendingCityName(null);
        }
    }, [cities, pendingCityName]);

    useEffect(() => {
        if (isProgrammaticUpdate.current) {
            return;
        }
        
        if (isInitialMount.current) {
            isInitialMount.current = false;
            if (hasInitialCoords.current) {
                return;
            }
        }

        if (geocodeTimeoutRef.current) {
            clearTimeout(geocodeTimeoutRef.current);
        }

        if (selectedState && selectedCity && selectedPostalCode) {
            geocodeTimeoutRef.current = setTimeout(() => {
                geocodeFromAddress();
            }, 800);
        }

        return () => {
            if (geocodeTimeoutRef.current) {
                clearTimeout(geocodeTimeoutRef.current);
            }
        };
    }, [selectedState, selectedCity, selectedPostalCode, selectedTownship, selectedStreet, selectedUnitNumber]);

    useEffect(() => {
        return () => {
            if (mapRef.current) {
                google.maps.event.clearInstanceListeners(mapRef.current);
                mapRef.current = null;
            }
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            if (geocodeTimeoutRef.current) {
                clearTimeout(geocodeTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        manualAddressRef.current = manualAddress;
    }, [manualAddress]);

    return (
        <section>
            <div className="mt-2 pt-5">
                <div className='md:flex gap-5'>
                    <div className='md:w-1/2'>
                        <div className="w-full space-y-1">
                            <div className="flex items-center justify-between">
                                <LabelWithAsterisk label="Location" className="text-black" />
                                <span className="text-sm text-gray-500">
                                    {manualAddress ? '' : `Can't find address? `}
                                    <button
                                    type="button"
                                    onClick={toggleManualAddress}
                                    className="text-blue-600 font-bold text-gray-700 hover:underline cursor-pointer transition">
                                        {manualAddress ? 'Search with map' : 'Enter manually'}
                                    </button>
                                </span>
                            </div>

                            <div className="relative mt-2">
                                {!manualAddress ? (
                                    <Autocomplete
                                        onLoad={(autocomplete) => {
                                            autocompleteRef.current = autocomplete;
                                        }}
                                        options={{
                                            componentRestrictions: { country: 'my' },
                                            types: [],
                                        }}
                                        onPlaceChanged={() => {
                                            setTimeout(() => {
                                                if(manualAddressRef.current) return
                                                if (!autocompleteRef.current) return;
                                                
                                                isProgrammaticUpdate.current = true;
                                                
                                                const place = autocompleteRef.current.getPlace();

                                                place.address_components?.forEach((component) => {
                                                    const types = component.types;

                                                    if (types.includes('street_number')) {
                                                        setSelectedUnitNumber(component.long_name);
                                                    }
                                                    if (types.includes('route')) {
                                                        setSelectedStreet(component.long_name);
                                                    }
                                                    if (types.includes('locality')) {
                                                        setPendingCityName(component.long_name);
                                                    }
                                                    if (types.includes('sublocality') || types.includes('sublocality_level_1')) {
                                                        setSelectedTownship(component.long_name);
                                                    }
                                                    if (types.includes('administrative_area_level_2')) {
                                                        setSelectedDistrict(component.long_name);
                                                    }
                                                    if (types.includes('administrative_area_level_3')) {
                                                        setSelectedSubdistrict(component.long_name);
                                                    }
                                                    if (types.includes('administrative_area_level_1')) {
                                                        const componentName = component.long_name.toLowerCase();
                                                        const state = states.find(x =>
                                                            componentName.includes(x.name.toLowerCase())
                                                        );
                                                        if (state) setSelectedState(state.id);
                                                    }
                                                    if (types.includes('postal_code')) {
                                                        setSelectedPostalCode(component.long_name);
                                                    }
                                                });

                                                setRawAddress(place.formatted_address ?? '');
                                                removeError('rawAddress');

                                                if (place.geometry?.location) {
                                                    const lat = place.geometry.location.lat();
                                                    const lng = place.geometry.location.lng();

                                                    setCoord({ lat, lng });
                                                    removeError('coordinates');

                                                    mapRef.current?.panTo({ lat, lng });
                                                    mapRef.current?.setZoom(17);
                                                }
                                                
                                                setTimeout(() => {
                                                    isProgrammaticUpdate.current = false;
                                                }, 100);
                                            }, 100)
                                        }}
                                    >
                                        <Input
                                        placeholder="Search address or pin on map"
                                        value={rawAddress}
                                        autoComplete="off"
                                        onChange={(e) => {
                                            setRawAddress(e.target.value);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                manualAddressRef.current = true
                                                setManualAddress(false);
                                                handleAddressSearch(e);
                                            }
                                        }}
                                        disabled={edit}
                                        className={`${
                                            validationErrors.includes('rawAddress') && 'border-red-400'
                                        }`}/>
                                    </Autocomplete>
                                ) : (
                                    <Input
                                    placeholder="Enter full address manually"
                                    value={rawAddress}
                                    onChange={(e) => setRawAddress(e.target.value)}
                                    className={`${
                                        validationErrors.includes('rawAddress') && 'border-red-400'
                                    }`}
                                    disabled={edit}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleAddressSearch();
                                        }
                                    }}/>
                                )}
                            </div>
                            <div className={`flex items-center mt-1 ${
                                validationErrors.includes('rawAddress')
                                    ? 'justify-between'
                                    : 'justify-end'
                            }`}>
                                {validationErrors.includes('rawAddress') && (
                                    <span className="text-red-400 text-xs">
                                        Address is required.
                                    </span>
                                )}
                                {!manualAddress && (
                                    <img
                                        src="/assets/img/powered_by_google_on_white.png"
                                        alt="Powered by Google"
                                        className="h-4 opacity-80"
                                    />
                                )}
                            </div>
                        </div>
                        {/* <div className="flex items-center gap-3 mt-4 p-3 bg-gray-50 rounded-lg border">
                            <Switch
                                checked={useTrueLocation}
                                onCheckedChange={(val) => {
                                    setUseTrueLocation(val);
                                    if(!val) {
                                        setRadius(50);
                                        if (circleRef.current) {
                                            circleRef.current.setRadius(50);
                                        }
                                    }
                                }}
                                disabled={!coord.lat || !coord.lng}
                            />
                            <div className="flex-1">
                                <Label className="text-black font-medium">Use true location</Label>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    Show exact pin instead of approximate area
                                </p>
                            </div>
                        </div> */}
                        {!useTrueLocation && (
                            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <LabelWithAsterisk label="Radius (meters)" className="text-black font-medium" />
                                <div className="flex items-center gap-3 mt-2">
                                    <Input
                                    type="number"
                                    min="1000"
                                    max="5000"
                                    step="50"
                                    value={radius || ''}
                                    onChange={(e) => {
                                        const val = Number(e.target.value);
                                        setRadius(val);
                                        if (val > 0) removeError('radius');
                                    }}
                                    className={`w-32 ${validationErrors.includes('radius') ? 'border-red-400' : ''}`}
                                    placeholder="e.g. 150"
                                    />
                                    <span className="text-sm text-gray-600">
                                        {radius ? `Approx ${(radius / 1000).toFixed(2)} km` : '-'}
                                    </span>
                                </div>
                                {validationErrors.includes('radius') && (
                                    <p className="text-red-400 text-xs mt-1">Radius is required</p>
                                )}
                                <p className="text-xs text-gray-500 mt-2">
                                    The map will show an approximate area circle. You can also drag the edge of the circle to adjust the radius.
                                </p>
                            </div>
                        )}

                        <div className='mt-4 rounded-[2vh] overflow-hidden relative'>
                            <div className='absolute top-4 left-4 z-10 flex flex-col gap-2'>
                                <button
                                onClick={toggleMapType}
                                className="cursor-pointer bg-white hover:bg-gray-100 text-gray-700 shadow-lg px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2">
                                    {mapType === 'roadmap' ? (
                                        <>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            Satellite
                                        </>
                                    ) : (
                                        <>
                                            <MapIcon size={16} />
                                            Map
                                        </>
                                    )}
                                </button>
                            </div>
                            <GoogleMap 
                                mapContainerStyle={{
                                    width: "100%",
                                    height: "500px",
                                }} 
                                onLoad={(map: google.maps.Map) => {
                                    mapRef.current = map;
                                }}
                                center={coord.lat && coord.lng ? { 
                                    lat: coord.lat, 
                                    lng: coord.lng 
                                } : { 
                                    lat: 4.2105, 
                                    lng: 101.9758 
                                }}
                                options={{
                                    mapTypeControl: false,
                                    streetViewControl: false,
                                    fullscreenControl: false,
                                    draggableCursor: 'pointer',
                                    draggingCursor: 'pointer',
                                    mapTypeId: mapType,
                                    styles: mapType === 'roadmap' ? [
                                        {
                                            featureType: "poi",
                                            elementType: "labels",
                                            stylers: [{ visibility: "off" }],
                                        },
                                        {
                                            featureType: "transit",
                                            elementType: "labels",
                                            stylers: [{ visibility: "off" }],
                                        },
                                        {
                                            featureType: "road",
                                            elementType: "labels.icon",
                                            stylers: [{ visibility: "off" }],
                                        },
                                        {
                                            featureType: "road",
                                            elementType: "labels.text.fill",
                                            stylers: [{ visibility: "off" }],
                                        },
                                    ] : undefined,
                                }}
                                zoom={coord.lat && coord.lng ? 15 : 6}
                                onClick={(e) => {
                                    if (e.latLng && !isDraggingRadius) {
                                        const lat = e.latLng.lat();
                                        const lng = e.latLng.lng();
                                        setCoord({ lat, lng });
                                        removeError('coordinates');
                                        reverseGeocode(lat, lng);
                                    }
                                }}>
                                {coord.lat && coord.lng && (
                                    <>
                                        {useTrueLocation ? (
                                            <Marker 
                                                position={{ lat: coord.lat, lng: coord.lng }}
                                                draggable={true}
                                                onDragEnd={(e) => {
                                                    if (e.latLng) {
                                                        const lat = e.latLng.lat();
                                                        const lng = e.latLng.lng();
                                                        setCoord({ lat, lng });
                                                        removeError('coordinates');
                                                        reverseGeocode(lat, lng);
                                                    }
                                                }} 
                                            />
                                        ) : (
                                            <>
                                                <Circle
                                                center={{ lat: coord.lat, lng: coord.lng }}
                                                radius={radius || 50}
                                                options={{
                                                    fillColor: '#4285F4',
                                                    fillOpacity: 0.2,
                                                    strokeColor: '#4285F4',
                                                    strokeOpacity: 0.8,
                                                    strokeWeight: 2,
                                                    editable: true,
                                                    draggable: false,
                                                }}
                                                onLoad={(circle) => {
                                                    circleRef.current = circle;
                                                    // Set initial radius if not already set
                                                    if (!radius) {
                                                        setRadius(50);
                                                    }
                                                }}
                                                onRadiusChanged={() => {
                                                    if (circleRef.current) {
                                                        let newRadius = Math.round(circleRef.current.getRadius());
                                                        if (newRadius < 1000) {
                                                            newRadius = 1000;
                                                            circleRef.current.setRadius(1000);
                                                        } else if (newRadius > 5000) {
                                                            newRadius = 5000;
                                                            circleRef.current.setRadius(5000);
                                                        }
                                                        setRadius(newRadius);
                                                        removeError('radius');
                                                    }
                                                }}
                                                onMouseDown={() => setIsDraggingRadius(true)}
                                                onMouseUp={() => setIsDraggingRadius(false)}
                                                />
                                                <Marker 
                                                    position={{ lat: coord.lat, lng: coord.lng }}
                                                    icon={{
                                                        path: google.maps.SymbolPath.CIRCLE,
                                                        scale: 6,
                                                        fillColor: '#4285F4',
                                                        fillOpacity: 1,
                                                        strokeColor: '#ffffff',
                                                        strokeWeight: 2,
                                                    }}
                                                    draggable={true}
                                                    onDragEnd={(e) => {
                                                        if (e.latLng) {
                                                            const lat = e.latLng.lat();
                                                            const lng = e.latLng.lng();
                                                            setCoord({ lat, lng });
                                                            removeError('coordinates');
                                                            reverseGeocode(lat, lng);
                                                        }
                                                    }} 
                                                />
                                            </>
                                        )}
                                    </>
                                )}
                            </GoogleMap>
                        </div>
                        {validationErrors.includes('coordinates') && (
                            <div className='text-red-400 text-xs mt-1'>Please select a location on the map.</div>
                        )}
                    </div>
                    <div className='md:w-1/2 mt-4 md:mt-0 space-y-5'>
                        <div className='bg-white border rounded-lg p-6 shadow-sm'>
                            <h3 className="text-lg font-semibold text-black mb-4">Address Details</h3>
                            
                            <div className="flex gap-2 w-full">
                                <div className='w-1/2'>
                                    <LabelWithAsterisk label='State' className='text-black'/>
                                    <Select 
                                        onValueChange={(val) => {
                                            setSelectedState(Number(val));
                                            setSelectedCity(null);
                                            setCities(null);
                                            removeError('state');
                                        }} 
                                        value={selectedState ? String(selectedState) : undefined}
                                        disabled={!coord.lat || !coord.lng || markAsDuplicate || edit}>
                                        <SelectTrigger className={`w-full mt-2 ${validationErrors.includes('state') && 'border-red-400'}`}>
                                            <SelectValue placeholder="Select state" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {states.map((state) => (
                                                <SelectItem key={state.id} value={String(state.id)}>{state.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {validationErrors.includes('state') && (
                                        <span className='text-xs text-red-400'>State is required.</span>
                                    )}
                                </div>
                                <div className='w-1/2'>
                                    <LabelWithAsterisk label='City' className='text-black'/>
                                    <Select 
                                        onValueChange={(val) => {
                                            setSelectedCity(Number(val));
                                            removeError('city');
                                        }} 
                                        value={selectedCity ? String(selectedCity) : undefined} 
                                        disabled={!cities || !coord.lat || !coord.lng || markAsDuplicate || edit}>
                                        <SelectTrigger className={`w-full mt-2 ${validationErrors.includes('city') && 'border-red-400'}`}>
                                            <SelectValue placeholder="Select city" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {cities?.map(city => (
                                                <SelectItem key={city.id} value={String(city.id)}>{city.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {validationErrors.includes('city') && (
                                        <span className='text-xs text-red-400'>City is required.</span>
                                    )}
                                </div>
                            </div>
                            <div className='mt-3'>
                                {
                                    shouldHideLandDetails && !noTownship ?
                                    <LabelWithAsterisk label='Township' className={`text-black mb-2`}/> :
                                    <Label className={`text-black mb-2`}>Township</Label>
                                }
                                <Input 
                                className={`mt-2 ${validationErrors.includes('township') && 'border-red-400'}`}
                                disabled={noTownship || !coord.lat || !coord.lng || markAsDuplicate || edit}
                                value={selectedTownship}
                                onChange={(e) => {
                                    setSelectedTownship(e.target.value);
                                    removeError('township');
                                }} />
                                {validationErrors.includes('township') && (
                                    <span className='text-red-400 text-xs'>Township is required.</span>
                                )}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                                <Checkbox 
                                    checked={noTownship} 
                                    onCheckedChange={(val) => {
                                        setNoTownship(!!val);
                                        setSelectedTownship('');
                                        if(val) {
                                            removeError('township');
                                        }
                                    }}
                                    disabled={!coord.lat || !coord.lng || markAsDuplicate || edit}
                                />
                                <Label className="text-black text-sm">My listing is not in any township.</Label>
                            </div>
                            <div className="mt-3">
                                {shouldHideLandDetails ? (
                                    <LabelWithAsterisk label="Street" className="text-black" />
                                ) : (
                                    <Label className="text-black">Street</Label>
                                )}
                                <Input 
                                className={`mt-2 ${validationErrors.includes('street') && 'border-red-400'}`}
                                value={selectedStreet}
                                onChange={(e) => {
                                    setSelectedStreet(e.target.value);
                                    removeError('street');
                                }}
                                disabled={!coord.lat || !coord.lng || markAsDuplicate || edit} />
                                {validationErrors.includes('street') && (
                                    <span className='text-red-400 text-xs'>Street is required.</span>
                                )}
                            </div>
                            <div className='mt-3 flex gap-2'>
                                <div className="w-1/2">
                                    {shouldHideLandDetails ? (
                                        <LabelWithAsterisk label="Unit Number" className="text-black" />
                                    ) : (
                                        <Label className="text-black">Unit Number</Label>
                                    )}
                                    <Input 
                                    className={`mt-2 ${validationErrors.includes('unitNumber') && 'border-red-400'}`}
                                    value={selectedUnitNumber}
                                    onChange={(e) => {
                                        setSelectedUnitNumber(e.target.value);
                                        removeError('unitNumber');
                                    }}
                                    disabled={!coord.lat || !coord.lng || markAsDuplicate || edit}/>
                                    {validationErrors.includes('unitNumber') && (
                                        <span className='text-red-400 text-xs'>Unit number is required.</span>
                                    )}
                                </div>
                                <div className="w-1/2">
                                    <LabelWithAsterisk label='Postcode' className='text-black'/>
                                    <Input 
                                    className={`mt-2 ${validationErrors.includes('postcode') && 'border-red-400'}`}
                                    value={selectedPostalCode}
                                    onKeyDown={Helper.allowOnlyNumbers}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (/^\d{0,5}$/.test(val)) {
                                            setSelectedPostalCode(val);
                                            removeError('postcode');
                                        }
                                    }}
                                    maxLength={5}
                                    disabled={!coord.lat || !coord.lng || markAsDuplicate || edit}/>
                                    {
                                        validationErrors.includes('postcode') && (
                                            <span className='text-red-400 text-xs'>Postcode is required.</span>
                                        )
                                    }
                                </div>
                            </div>
                            {
                                coord.lat && coord.lng && (
                                    <div className='mt-4 p-3 bg-gray-50 rounded-lg'>
                                        <Label className="text-black font-semibold">Coordinates</Label>
                                        <div className="text-sm text-gray-600 mt-1">
                                            <div>Latitude: {coord.lat.toFixed(6)}</div>
                                            <div>Longitude: {coord.lng.toFixed(6)}</div>
                                        </div>
                                    </div>
                                )
                            }
                        </div>

                        {shouldHideLandDetails ? (
                            <div className="bg-white border rounded-lg p-6 shadow-sm">
                                <h4 className="text-md font-semibold text-black mb-3">Utility Bills</h4>
                                <div className="space-y-4">
                                    <div>
                                        <LabelWithAsterisk label="Electricity Bill" className="text-black" />
                                        <Input
                                            type="file"
                                            accept="image/jpeg, image/jpg, image/png, application/pdf"
                                            className={`mt-2 cursor-pointer ${validationErrors.includes('electricityBill') ? 'border-red-400' : ''}`}
                                            onChange={(e) => {
                                                const file = e.target.files?.[0] || null;
                                                handleFileChange('electricity', file);
                                            }}
                                            disabled={!coord.lat || !coord.lng}
                                        />
                                        {validationErrors.includes('electricityBill') && (
                                            <p className="text-red-400 text-xs mt-1">Electricity bill is required.</p>
                                        )}
                                        {listing?.electricityBill && !electricityBill && (
                                            <div className="mt-3 border rounded p-2 bg-gray-50">
                                                {
                                                    Helper.isImage(listing.electricityBill) ? (
                                                        <img
                                                        src={`${import.meta.env.VITE_AWS_S3}/${listing.electricityBill}`}
                                                        alt="Electricity Bill"
                                                        className="max-h-64 w-auto rounded object-contain border"
                                                        />
                                                    ) : (
                                                        <iframe
                                                        src={`${import.meta.env.VITE_AWS_S3}/${listing.electricityBill}`}
                                                        title="Electricity Bill"
                                                        className="w-full h-64 rounded border"/>
                                                    )
                                                }
                                            </div>
                                        )}
                                    </div>

                                    {/* Water Bill */}
                                    <div>
                                        <LabelWithAsterisk label="Water Bill" className="text-black" />
                                        <Input
                                            type="file"
                                            accept="image/jpeg, image/jpg, image/png, application/pdf"
                                            className={`mt-2 cursor-pointer ${validationErrors.includes('waterBill') ? 'border-red-400' : ''}`}
                                            onChange={(e) => {
                                                const file = e.target.files?.[0] || null;
                                                handleFileChange('water', file);
                                            }}
                                            disabled={!coord.lat || !coord.lng}
                                        />
                                        {validationErrors.includes('waterBill') && (
                                            <p className="text-red-400 text-xs mt-1">Water bill is required.</p>
                                        )}
                                        
                                        {listing?.waterBill && !waterBill && (
                                            <div className="mt-3 border rounded p-2 bg-gray-50">
                                                {Helper.isImage(listing.waterBill) ? (
                                                    <img
                                                        src={`${import.meta.env.VITE_AWS_S3}/${listing.waterBill}`}
                                                        alt="Water Bill"
                                                        className="max-h-64 w-auto rounded object-contain"
                                                    />
                                                ) : (
                                                    <iframe
                                                        src={`${import.meta.env.VITE_AWS_S3}/${listing.waterBill}`}
                                                        title="Water Bill"
                                                        className="w-full h-64 rounded border"
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <Alert className="mt-6 border-blue-200 bg-blue-50 text-blue-600">
                                    <Info className="h-4 w-4" />
                                    <AlertTitle className="font-semibold">
                                        Please upload utility bills
                                    </AlertTitle>
                                    <AlertDescription className="text-sm mt-1">
                                        Please provide clear copies of your electricity and water bills for verification.
                                    </AlertDescription>
                                </Alert>
                            </div>
                        ) : (
                            <div className="bg-white border rounded-lg p-6 shadow-sm">
                                <h4 className="text-md font-semibold text-black mb-3">Land Details</h4>
                                <div>
                                    <LabelWithAsterisk label="Lot No." className="text-black" />
                                    <Input
                                    className={`mt-2 ${validationErrors.includes('lotNo') ? 'border-red-400' : ''}`}
                                    value={lotNo}
                                    onChange={(e) => {
                                        setLotNo(e.target.value);
                                        removeError('lotNo');
                                    }}
                                    placeholder="Enter lot number"
                                    disabled={!coord.lat || !coord.lng || markAsDuplicate || edit} />
                                    {
                                        validationErrors.includes('lotNo') && (
                                            <p className="text-red-400 text-xs mt-1">Lot number is required.</p>
                                        )
                                    }
                                </div>
                                <div className="flex gap-2 w-full mt-3">
                                    <div className="w-1/2">
                                        <LabelWithAsterisk label="Daerah" className="text-black" />
                                        <Select
                                        onValueChange={(val) => {
                                            setSelectedDistrict(val)
                                            removeError('district');
                                        }}
                                        value={selectedDistrict || undefined}
                                        disabled={!coord.lat || !coord.lng || markAsDuplicate || edit}>
                                                <SelectTrigger className={`w-full mt-2 ${validationErrors.includes('district') ? 'border-red-400' : ''}`}>
                                                    <SelectValue placeholder="Select district" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                {
                                                    (selectedState
                                                        ? districts.filter((district) => district.stateId === selectedState)
                                                        : districts
                                                    ).map((district) => (
                                                        <SelectItem key={district.id} value={String(district.id)}>
                                                            {district.name}
                                                        </SelectItem>
                                                    ))
                                                }
                                                </SelectContent>
                                        </Select>
                                        {
                                            validationErrors.includes('district') && (
                                                <p className="text-red-400 text-xs mt-1">District is required.</p>
                                            )
                                        }
                                    </div>

                                    <div className="w-1/2">
                                        <LabelWithAsterisk label="Bandar/Pekan/Mukim" className="text-black" />
                                        <Select
                                        onValueChange={(val) => {
                                            setSelectedSubdistrict(val);
                                            removeError('subdistrict');
                                        }}
                                        value={selectedSubdistrict || undefined}
                                        disabled={!coord.lat || !coord.lng || markAsDuplicate || edit}>
                                            <SelectTrigger 
                                            className={`w-full mt-2 ${validationErrors.includes('subdistrict') ? 'border-red-400' : ''}`}
                                            disabled={!selectedDistrict}>
                                                <SelectValue placeholder="Select subdistrict" />
                                            </SelectTrigger>
                                            <SelectContent>
                                            {
                                                subdistricts.filter(x => x.districtId === Number(selectedDistrict)).map((sd) => (
                                                    <SelectItem key={sd.id} value={String(sd.id)}>
                                                        {sd.name}
                                                    </SelectItem>
                                                ))
                                            }
                                            </SelectContent>
                                        </Select>
                                        {
                                            validationErrors.includes('subdistrict') && (
                                                <p className="text-red-400 text-xs mt-1">Subdistrict is required.</p>
                                            )
                                        }
                                    </div>
                                </div>
                                <div className="flex gap-2 w-full mt-3">
                                    <div className="w-1/2">
                                        <LabelWithAsterisk label="Size of Land" className="text-black" />
                                        <Input
                                        className={`mt-2 ${validationErrors.includes('sizeOfLand') ? 'border-red-400' : ''}`}
                                        value={sizeOfLand}
                                        onChange={(e) => {
                                            setSizeOfLand(e.target.value);
                                            removeError('sizeOfLand');
                                        }}
                                        placeholder="e.g., 1000 sq ft"
                                        disabled={!coord.lat || !coord.lng || markAsDuplicate || edit}/>
                                        {
                                            validationErrors.includes('sizeOfLand') && (
                                                <p className="text-red-400 text-xs mt-1">Size of land is required.</p>
                                            )
                                        }
                                    </div>
                                    <div className="w-1/2">
                                        <LabelWithAsterisk label="Jenis Hakmilik" className="text-black" />
                                        <Select
                                        onValueChange={(val) => {
                                            setSelectedTitleTypeCode(val);
                                            removeError('landTitleType');
                                        }}
                                        value={selectedTitleTypeCode || undefined}
                                        disabled={!coord.lat || !coord.lng || markAsDuplicate || edit} >
                                            <SelectTrigger className={`w-full mt-2 ${validationErrors.includes('landTitleType') ? 'border-red-400' : ''}`}>
                                                <SelectValue placeholder="Select title type" />
                                            </SelectTrigger>
                                            <SelectContent>
                                            {
                                                (selectedState
                                                    ? landTitleTypes.filter((type) => type.stateId === selectedState)
                                                    : landTitleTypes
                                                ).map((type) => (
                                                    <SelectItem key={type.id} value={String(type.id)}>
                                                        {type.name}
                                                    </SelectItem>
                                                ))
                                            }
                                            </SelectContent>
                                        </Select>
                                        {
                                            validationErrors.includes('landTitleType') && (
                                                <p className="text-red-400 text-xs mt-1">Land title type is required.</p>
                                            )
                                        }
                                    </div>
                                </div>
                                <div  className="flex gap-2 w-full mt-3">
                                    <div className="w-1/2">
                                        <Label className='text-black'>JUPEM / MyLot No. </Label>
                                        <Input
                                        className={`mt-2`}
                                        value={jupemNo}
                                        onChange={(e) => setJupemNo(e.target.value)}
                                        placeholder="Enter JUPEM number"
                                        disabled={!coord.lat || !coord.lng || markAsDuplicate || edit}/>
                                    </div>
                                    <div className="w-1/2">
                                        <LabelWithAsterisk label="No. Hakmilik" className="text-black" />
                                        <Input
                                        className={`mt-2 ${validationErrors.includes('ownershipNumber') ? 'border-red-400' : ''}`}
                                        value={ownershipNumber}
                                        onChange={(e) => {
                                            setOwnershipNumber(e.target.value);
                                            removeError('ownershipNumber');
                                        }}
                                        placeholder="Enter ownership number"
                                        disabled={!coord.lat || !coord.lng || markAsDuplicate || edit}/>
                                        {
                                            validationErrors.includes('ownershipNumber') && (
                                                <p className="text-red-400 text-xs mt-1">Ownership number is required.</p>
                                            )
                                        }
                                    </div>
                                </div>
                                <div className="mt-3">
                                    <LabelWithAsterisk label="Proof" className="text-black" />
                                    <Input
                                    type='file'
                                    className={`mt-2 ${validationErrors.includes('proof') ? 'border-red-400' : ''}`}
                                    onChange={(e) => {
                                        if(e.target.files) {
                                            setProof?.(e.target.files[0]);
                                            removeError('proof');
                                        }
                                    }}
                                    onClick={(e) => {
                                        setProof?.(null);
                                    }}
                                    onFocus={(e) => {
                                        setProof?.(null);
                                    }}
                                    accept="image/jpeg, image/jpg, image/png, application/pdf"
                                    disabled={!coord.lat || !coord.lng}/>
                                    {
                                        listing?.proof && (
                                            <div className="mt-3 border rounded p-2 bg-gray-50">
                                                {
                                                    Helper.isImage(listing.proof) ? (
                                                        <img
                                                        src={`${import.meta.env.VITE_AWS_S3}/${listing.proof}`}
                                                        alt="Proof"
                                                        className="max-h-64 w-auto rounded object-contain"/>
                                                    ) : (
                                                        <iframe
                                                        src={`${import.meta.env.VITE_AWS_S3}/${listing.proof}`}
                                                        title="Proof document"
                                                        className="w-full h-64 rounded border"/>
                                                    )
                                                }
                                            </div>
                                        )
                                    }
                                    {
                                        validationErrors.includes('proof') && (
                                            <p className="text-red-400 text-xs mt-1">Proof is required.</p>
                                        )
                                    }
                                </div>
                                <Alert className="mt-6 border-blue-200 bg-blue-50 text-blue-600">
                                    <Info className="h-4 w-4" />
                                    <AlertTitle className="font-semibold">
                                        Please verify your land details
                                    </AlertTitle>
                                    <AlertDescription className="text-sm mt-1">
                                        Please review and ensure the accuracy of all information provided.
                                    </AlertDescription>
                                </Alert>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {loadingDetails ? 'Loading...' : listingDetails?.title || 'Listing Details'}
                        </DialogTitle>
                    </DialogHeader>
                    {
                        loadingDetails ? 
                        (
                            <div className='text-center py-8'>
                                <div className='text-gray-600'>Loading listing details...</div>
                            </div>
                        ) 
                        : listingDetails ? (
                            <>
                                <Details listing={listingDetails} size='sm'/>
                                <div className='flex justify-end'>
                                    <Button size={'sm'} 
                                    disabled={marking}
                                    onClick={markingAsDuplicate}>Mark as same listing {
                                        marking && (
                                            <Spinner />
                                        )
                                    }</Button>
                                </div>
                            </>
                        ) : null
                    }
                </DialogContent>
            </Dialog>
        </section>
    );
});

export default LocationMappingDetails;